// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { CUSTOMIZER_DRILL_EVENT } from '@theme/custom.customizer-drill';
import { holdDialogCancel } from '@theme/custom.customizer-layer';
import { quoteMetalSurchargeLabels, quoteSurchargeLabels } from '@theme/custom.customizer-currency';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */
/** @typedef {import('@theme/events').CustomizerUpdateEvent} CustomizerUpdateEvent */
/** @typedef {import('@theme/custom.customizer-drill').DrillAdvance} DrillAdvance */
/** @typedef {import('@theme/custom.customizer-commit').CartFailure} CartFailure */

/**
 * The owning `<custom-customizer>`, as this footer reads it.
 * @typedef {Element & { store?: CustomizerStore, drillAdvance?: DrillAdvance | null }} CustomizerHost
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} title - The step heading; focus moves here on step change
 *   (tabindex="-1").
 * @property {HTMLElement} count - The "current/total" counter (e.g. "1/9").
 * @property {HTMLElement} nextLabel - The footer's next-step label (or the finish label on the
 *   last step).
 * @property {HTMLElement} [nextPrefix] - The footer's "Next:" prefix; hidden on the last step.
 * @property {HTMLElement} [back] - The back-nav button; hidden on the first step.
 * @property {HTMLElement} [price] - The footer price pill; the live variant price + engraving
 *   surcharge.
 * @property {HTMLButtonElement} [cta] - The footer Next/Finish button; visually disabled
 *   (aria-disabled + CSS, stays keyboard-focusable) on a required variant step with no pick
 *   (CustomizerStore#canAdvance) or while the current step is incomplete
 *   (CustomizerStore#currentStepIncomplete). A drilled view holding the CTA
 *   (CustomizerDrillComponent#advanceIntent) supplies both the label and the completeness.
 * @property {HTMLButtonElement} [skip] - The footer "No, Thank You" chip; shown on skippable,
 *   non-face steps (faces carry their own in-menu skip). Routes to CustomizerStore#skip.
 * @property {HTMLElement} [commitError] - The add-to-cart failure banner above the CTA,
 *   hidden until an add fails.
 * @property {HTMLElement} [commitErrorText] - The banner's message: the server's when the response
 *   carried one, the markup's own generic copy otherwise.
 * @property {HTMLElement} [committingLabel] - The CTA's busy text ("Adding to cart..." /
 *   "Updating..."); swapped per render off the host's edit flag.
 * @property {HTMLElement} [body] - The shared scrollable step body; scrolls back to the top on a
 *   step change so a new step never opens at the previous step's scroll offset.
 * @property {HTMLButtonElement} [progressTrigger] - The header's MY PROGRESS button; opens the
 *   progress drawer and takes focus back when it closes.
 * @property {HTMLElement} [progressDrawer] - The My Progress drawer (backdrop + sheet,
 *   snippets/custom.customizer-progress.liquid); toggled via [hidden] so its slide runs both ways.
 * @property {HTMLButtonElement} [progressClose] - The drawer's close button; focus lands here on
 *   open.
 * @property {HTMLElement} [progressCount] - The drawer title's "current/total" counter.
 * @property {HTMLElement} liveRegion - Visually-hidden polite live region for step-change
 *   announcements.
 *
 * @extends Component<Refs>
 */
class CustomizerStep extends Component {
  requiredRefs = ['title', 'count', 'nextLabel', 'liveRegion'];

  /** @type {string | null} */
  #renderedStep = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#handleUpdate);
    this.addEventListener(CUSTOMIZER_DRILL_EVENT, this.#handleDrill);
    // Real listener (the on: framework has no pointerdown): remembers whether the last press
    // began on the progress drawer's dim, see progressBackdrop().
    this.addEventListener('pointerdown', this.#trackProgressPress);
    // Outside the shop's currency Liquid leaves every option's "+$X" for the rate to fill.
    quoteSurchargeLabels(this, this.#quoteSurcharge);
    this.#renderFromHost();
  }

  /** @param {number} cents - Already in the shopper's currency. */
  #quoteSurcharge = (cents) => {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    return `${this.dataset.surchargePrefix ?? '+'}${formatMoney(cents, format, currency, true)}`;
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#handleUpdate);
    this.removeEventListener(CUSTOMIZER_DRILL_EVENT, this.#handleDrill);
    this.removeEventListener('pointerdown', this.#trackProgressPress);
    // The progress drawer's document/dialog listeners only exist while it is open, so drop them
    // if the component is torn down mid-open (a section re-render).
    this.#releaseProgressCancel();
    document.removeEventListener('keydown', this.#progressKeydown, { capture: true });
    document.removeEventListener('focusin', this.#progressFocusIn);
  }

  /** @param {Event} event */
  #handleUpdate = (event) => {
    const { configId } = /** @type {CustomizerUpdateEvent} */ (event).detail;
    if (configId !== this.#configId) return;
    this.#renderFromHost();
  };

  /**
   * @returns {string} The owning customizer instance's config id (for scoping document events),
   *   read off the host so a descendant can never guard on a second copy of it.
   */
  get #configId() {
    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    return host?.dataset.configId ?? '';
  }

  /** A drill often writes no control value, so it never fires `customizer:update`: re-render
   *  here or Back's visibility misses the drill-in. No config-id guard: it only bubbles here. */
  #handleDrill = () => {
    this.#renderFromHost();
  };

  #renderFromHost() {
    const store = this.#store;
    if (!store) return;
    // A metal pick re-prices every "+$X" that differs by metal.
    quoteMetalSurchargeLabels(this, this.#quoteSurcharge, store.metalNames);
    this.#render(store);
  }

  /** @returns {CustomizerHost | null} */
  get #host() {
    return /** @type {CustomizerHost | null} */ (this.closest('custom-customizer'));
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    return this.#host?.store;
  }

  /** Lets go of the host dialog's `cancel`, held while the progress drawer is open. */
  #releaseProgressCancel = () => {};

  /** Bound from the step header's MY PROGRESS button via `on:click="/openProgress"`. */
  openProgress() {
    const drawer = this.refs.progressDrawer;
    if (!drawer || !drawer.hasAttribute('hidden')) return;

    drawer.removeAttribute('hidden');
    this.refs.progressTrigger?.setAttribute('aria-expanded', 'true');

    // dialog.js handles Escape in its own bubble keydown, so the native `cancel` never fires: a
    // document CAPTURE listener wins; `cancel` stays only for keydown-less closes (CloseWatcher).
    document.addEventListener('keydown', this.#progressKeydown, { capture: true });
    this.#releaseProgressCancel = holdDialogCancel(this, () => this.closeProgress());
    // The native dialog traps focus in the customizer, not in this inner modal, so keep Tab inside
    // the sheet ourselves while it is open (aria-modal already scopes screen readers to it).
    document.addEventListener('focusin', this.#progressFocusIn);
    this.refs.progressClose?.focus();
  }

  /** Close the drawer and hand focus back to the trigger. Bound to the drawer's X via
   *  `on:click="/closeProgress"`; also the Esc and backdrop-click path. */
  closeProgress() {
    this.#closeProgress(true);
  }

  /**
   * Backdrop click-to-close. Two traps make a naive target check wrong: the delegated `on:`
   * proxy (component.js) rewrites `event.target` to the BOUND element (this drawer root) for
   * every click that resolves here — so the true origin is read off `composedPath()`, which the
   * proxy binds to the raw event — and a press that starts in the sheet but releases over the
   * dim (a text-selection drag) retargets the click to their common ancestor, the drawer root
   * itself. Close only when both the click AND the preceding press originated on the dim.
   * @param {Event} event
   */
  progressBackdrop(event) {
    if (event.composedPath()[0] === this.refs.progressDrawer && this.#pressOnDim) {
      this.closeProgress();
    }
  }

  /**
   * A row's Edit: jump the wizard to that (already-visited) step. The store broadcast then swaps
   * the panel and focuses the incoming step's title, so focus is NOT returned to the trigger.
   * @param {Event} event
   */
  editStep(event) {
    const row = /** @type {Element} */ (event.target).closest('[data-progress-step]');
    const id = row?.getAttribute('data-progress-step');
    const store = this.#store;
    if (!id || !store) return;

    const index = store.stepOrder.indexOf(id);
    if (index < 0) return;

    // Drop the trap before navigation so the incoming title can receive focus.
    document.removeEventListener('focusin', this.#progressFocusIn);
    // A successful jump focuses its title; a refused jump restores focus to the trigger.
    // Otherwise focus would fall into the hidden drawer.
    const moved = store.goTo(index);
    this.#closeProgress(!moved);
  }

  /** @param {boolean} restoreFocus - False when navigation is about to move focus itself. */
  #closeProgress(restoreFocus) {
    // Teardown follows opening, even if a section morph already hid the drawer.
    // Removals are idempotent and prevent document-listener leaks.
    this.#releaseProgressCancel();
    this.#releaseProgressCancel = () => {};
    document.removeEventListener('keydown', this.#progressKeydown, { capture: true });
    document.removeEventListener('focusin', this.#progressFocusIn);

    const drawer = this.refs.progressDrawer;
    if (!drawer || drawer.hasAttribute('hidden')) return;

    drawer.setAttribute('hidden', '');
    this.refs.progressTrigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.refs.progressTrigger?.focus();
  }

  /**
   * Escape, caught at document level in the CAPTURE phase so it wins over the overlay
   * DialogComponent's own bubble keydown (which would close the whole customizer).
   * @param {Event} event
   */
  #progressKeydown = (event) => {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return;
    event.stopPropagation();
    event.preventDefault();
    this.closeProgress();
  };

  /** @param {Event} event */
  #progressFocusIn = (event) => {
    const drawer = this.refs.progressDrawer;
    if (drawer && event.target instanceof Node && !drawer.contains(event.target)) {
      this.refs.progressClose?.focus();
    }
  };

  /** True when the last pointer press began on the drawer's dim itself (not inside the sheet). */
  #pressOnDim = false;

  /** Real pointerdown listener (see connectedCallback); feeds {@link progressBackdrop}.
   * @param {Event} event */
  #trackProgressPress = (event) => {
    this.#pressOnDim = event.target === this.refs.progressDrawer;
  };

  /** @param {CustomizerStore} store */
  #render(store) {
    if (store.isEmpty) return;

    const order = store.stepOrder;
    const current = store.currentStep;
    const index = current ? order.indexOf(current) : -1;
    if (index < 0 || !current) return;

    const total = order.length;
    // Look steps up by id, not by order index: the store's navigation order EXCLUDES hidden
    // single-value variant steps, so it does not align 1:1 with configSchema.steps.
    const steps = store.configSchema?.steps ?? [];
    const stepDoc = steps.find((candidate) => candidate.id === current);
    const nextId = order[index + 1];
    const label = stepDoc?.label ?? '';
    const nextLabel = nextId
      ? steps.find((candidate) => candidate.id === nextId)?.label
      : undefined;
    const { title, count, nextLabel: nextLabelEl, nextPrefix } = this.refs;

    title.textContent = label;
    count.textContent = `${index + 1}/${total}`;
    this.style.setProperty('--customizer-step-progress', `${((index + 1) / total) * 100}%`);

    // Mirror lifecycle into server-rendered progress rows; `data-state` drives their styling.
    // Keep the drawer counter aligned with the header.
    for (const row of this.querySelectorAll('[data-progress-step]')) {
      const id = row.getAttribute('data-progress-step') ?? '';
      const state = store.steps[id]?.status ?? 'pending';
      row.setAttribute('data-state', state);
      if (state === 'active') row.setAttribute('aria-current', 'step');
      else row.removeAttribute('aria-current');
      const stateText = row.querySelector('[data-progress-state]');
      if (stateText instanceof HTMLElement) {
        stateText.textContent =
          state === 'complete' || state === 'skipped' ? ` ${stateText.dataset[state] ?? ''}` : '';
      }
    }
    const progressCount = this.refs.progressCount;
    if (progressCount) progressCount.textContent = `${index + 1}/${total}`;

    const activePanel = this.querySelector(`[data-customizer-step="${current}"]`);

    // Show step skip only when skippable, non-final, and not owned by in-panel controls.
    // Required steps never expose it; `store.skip()` would reject them.
    const ownsSkip = !!activePanel?.querySelector('[data-owns-skip]');
    const showSkip = store.currentStepSkippable && !ownsSkip && !store.isLastStep;
    this.refs.skip?.toggleAttribute('hidden', !showSkip);

    // Back also returns a drilled-in first step to its root view.
    // Otherwise products without variant axes would strand the shopper.
    const drilledIn = !!activePanel?.querySelector('[data-drilled]');
    this.refs.back?.toggleAttribute('hidden', index <= 0 && !drilledIn);

    for (const panel of this.querySelectorAll('[data-customizer-step]')) {
      panel.toggleAttribute('hidden', panel.getAttribute('data-customizer-step') !== current);
    }

    // A drilled view can own the CTA (drill protocol): its press stays inside this step, so the
    // footer names where THAT lands instead of promising the next step it is not going to.
    const drillAdvance = this.#host?.drillAdvance ?? null;

    // Edit mode uses Update labels; Add to Cart would promise a second line.
    // Read each render because the host clears an unavailable or spent edit.
    const editing = !!this.closest('custom-customizer[data-editing]');
    const ctaLabel = drillAdvance?.label || nextLabel;
    if (ctaLabel) {
      nextPrefix?.removeAttribute('hidden');
      nextLabelEl.textContent = ctaLabel;
    } else {
      nextPrefix?.setAttribute('hidden', '');
      nextLabelEl.textContent =
        (editing ? this.dataset.labelUpdate : this.dataset.labelFinish) ?? '';
    }
    const committingLabel = this.refs.committingLabel;
    if (committingLabel) {
      const busyLabel = editing ? this.dataset.labelUpdating : this.dataset.labelAdding;
      if (busyLabel) committingLabel.textContent = busyLabel;
    }

    this.#renderPrice(store);

    // Same gates as `advance()`; `aria-disabled` keeps the CTA focusable. A drill that owns the
    // CTA writes the completeness itself, so its claim stands in for that gate.
    const cta = this.refs.cta;
    if (cta) {
      const blocked = !store.canAdvance || (!drillAdvance && store.currentStepIncomplete());
      cta.classList.toggle('custom-customizer-step__cta--disabled', blocked);
      cta.setAttribute('aria-disabled', String(blocked));
    }

    if (this.#renderedStep !== null && this.#renderedStep !== current) {
      // Reset shared scrolling for the incoming step; its content has no continuity to animate.
      this.refs.body?.scrollTo({ top: 0 });
      this.#announce(index + 1, total, label);
      title.focus();
    }
    this.#renderedStep = current;
  }

  /**
   * Render the footer price pill = the selected variant's price (or the seeded base price before a
   * variant resolves) + the live engraving/options surcharge. Display-only; the Cart Transform
   * enforces the trusted price (see docs/customizer/engine/06-pricing-cart-transform.md).
   *
   * Carries the "From" prefix only while NO variant has resolved, where the base is the floor
   * the page quoted rather than a configuration anyone chose. Once one resolves the number is
   * exact, even on the metal step: picking a metal back-fills the other axes to their cheapest
   * row (`#hydrateVariantAxes`), so a size really is committed by then. The prefix exists only on
   * a product whose page hides an axis (`data-label-from`, from custom.customizer-from-price), so
   * an ordinary product never gains it.
   * @param {CustomizerStore} store
   */
  #renderPrice(store) {
    const priceEl = this.refs.price;
    if (!priceEl) return;

    const base = store.selectedVariant?.price ?? Number(this.dataset.basePrice);
    if (!Number.isFinite(base)) return; // no price data → keep the server-rendered fallback

    const total = base + store.surchargeCents();
    const amount = formatMoney(
      total,
      this.dataset.moneyFormat || '{{amount}}',
      this.dataset.currency || 'USD'
    );

    const fromLabel = this.dataset.labelFrom;
    priceEl.textContent =
      fromLabel && store.selectedVariant === null ? `${fromLabel} ${amount}` : amount;
  }

  /**
   * The footer's own generic add-to-cart failure copy, remembered on first use so a server message
   * (`Sold out`, a quantity cap) never sticks around as the fallback for a later timeout.
   * @type {string | null}
   */
  #commitErrorFallback = null;

  /**
   * Show why the add failed, above the CTA the shopper just pressed. Called by
   * custom.customizer.js when the cart rejects the add; Horizon's own error text renders inside the
   * buy-buttons block on the PDP, which the shopper cannot see behind the overlay.
   * @param {string} [message] - The server's message, when the response carried one.
   * @param {CartFailure} [reason] - A failure with its own copy, which wins over the message.
   */
  showCommitError(message, reason) {
    const { commitError, commitErrorText } = this.refs;
    if (!commitError || !commitErrorText) return;

    // Unhide first: a live region's text change goes unannounced while its subtree is hidden.
    commitError.hidden = false;
    this.#commitErrorFallback ??= commitErrorText.textContent?.trim() ?? '';
    const reasonText = reason ? commitErrorText.getAttribute(`data-${reason}-message`) : '';
    commitErrorText.textContent = reasonText || message || this.#commitErrorFallback;
  }

  /** Drop the failure line. If focus is on its X, hiding it would drop focus to `<body>`
   *  mid-dialog, so the CTA (focusable even when blocked) takes focus first. */
  clearCommitError() {
    const { commitError, cta } = this.refs;
    if (!commitError) return;
    if (commitError.contains(document.activeElement)) cta?.focus({ preventScroll: true });
    commitError.hidden = true;
  }

  /**
   * @param {number} current
   * @param {number} total
   * @param {string} name
   */
  #announce(current, total, name) {
    const template = this.dataset.announcement;
    if (!template) return;
    this.refs.liveRegion.textContent = template
      .replace('[current]', String(current))
      .replace('[total]', String(total))
      .replace('[name]', () => name);
  }
}

if (!customElements.get('custom-customizer-step')) {
  customElements.define('custom-customizer-step', CustomizerStep);
}
